import { Component, Prop, h, State, Watch, getAssetPath, Build, Element } from '@stencil/core';


/**
 * Icon component for displaying SVG icons with lazy loading support.
 * Icons can be organized in categories using the format: 'category--name'.
 * For example: 'interface--close' will load from '/assets/icons/interface/close.svg'
 */
const COMPONENT_NAME = 'me-icon';

/**
 * @example
 *   <me-icon name="interface--check"></me-icon>
 */
@Component({
  tag: 'me-icon',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Icon {
  @Element() host!: HTMLElement;

  private observer: IntersectionObserver;
  private iconContainer?: HTMLDivElement;

  /**
   * Guard flag to prevent duplicate loadIcon() calls (e.g. from IntersectionObserver + manualViewportCheck racing).
   */
  private iconLoading = false;

  /**
   * Name of the icon to display.
   * Use format 'category--name' for categorized icons (e.g., 'interface--close')
   * or just 'name' for icons in the root directory.
   * Also supports inline SVG strings (e.g., '<svg>...</svg>') or CDN URLs (e.g., 'https://...').
   */
  @Prop({ reflect: true }) name!: string;

  /**
   * Optional color override for the icon.
   * Applies to the SVG fill property.
   */
  @Prop() color?: string;

  /**
   * Whether to load the icon immediately without waiting for it to enter viewport.
   * @deprecated Use lazy="false" or inline="true" instead
   */
  @Prop() isPreload?: boolean = false;

  /**
   * Enable lazy loading (default: true). Accepts boolean or string ('true'/'false').
   * When true, icons load only when near viewport. When false, loads immediately.
   */
  @Prop() lazy: boolean | string = true;

  /**
   * Inline the SVG during server-side rendering (SSR).
   * When true, the SVG is fetched from CDN on the server and inlined into HTML,
   * eliminating network requests for critical above-the-fold icons.
   * Only affects SSR - on client-side, behaves like lazy="false".
   */
  @Prop({ reflect: true }) inline?: boolean = false;

  /**
   * Width of the icon container. Accepts any CSS unit (px, rem, %, etc.).
   * Numeric values without units will be treated as pixels.
   * Prevents CLS by reserving space before icon loads.
   */
  @Prop() width?: string;

  /**
   * Height of the icon container. Accepts any CSS unit (px, rem, %, etc.).
   * Numeric values without units will be treated as pixels.
   * Prevents CLS by reserving space before icon loads.
   */
  @Prop() height?: string;

  /**
   * Additional CSS class names to apply to the icon container.
   */
  @Prop() class?: string;

  /**
   * Accessible alt text for URL-based icons rendered as <img>.
   * Required for meaningful icons; falls back to a humanized version of the
   * icon file name when not provided, since empty alt is not allowed.
   */
  @Prop() alt?: string;

  /**
   * Internal state to store the loaded SVG content.
   */
  @State() svgContent: string = '';

  /**
   * Internal state to store the icon type (svg, url, or name).
   */
  @State() iconType: 'svg' | 'url' | 'name' = 'name';

  /**
   * Normalize the lazy prop to a boolean value.
   */
  private get isLazy(): boolean {
    // inline mode disables lazy loading (for SSR and client-side)
    if (this.inline) return false;

    // isPreload overrides lazy setting for backward compatibility
    if (this.isPreload) return false;

    if (typeof this.lazy === 'string') {
      return this.lazy === 'true';
    }
    return !!this.lazy;
  }

  /**
   * Normalizes size values by adding 'px' to numeric strings.
   * Returns undefined if value is not provided.
   */
  private normalizeSize(value?: string): string | undefined {
    if (!value) return undefined;

    // Check if value is a number without units
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue) && value.trim() === String(numericValue)) {
      return `${numericValue}px`;
    }

    return value;
  }

  /**
   * Detects the type of icon based on the name prop.
   * Returns 'svg' for inline SVG strings, 'url' for URLs, or 'name' for icon names.
   */
  private detectIconType(): 'svg' | 'url' | 'name' {
    if (!this.name) return 'name';

    const trimmed = this.name.trim();

    // Check if it's a URL first (most specific)
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      return 'url';
    }

    // Check if it's an inline SVG (check for SVG tags, including multi-line)
    if (trimmed.startsWith('<svg') || trimmed.startsWith('<SVG') || /<svg[\s>]/i.test(trimmed)) {
      return 'svg';
    }

    return 'name';
  }

  /**
   * Initializes icon type and processes inline SVG synchronously.
   * This runs before the first render to avoid extra re-renders.
   */
  async componentWillLoad() {
    const detectedType = this.detectIconType();
    // Only update @State if it differs from the default to avoid a no-op re-render
    if (detectedType !== this.iconType) {
      this.iconType = detectedType;
    }

    // For inline SVG, process synchronously to avoid re-render warning
    if (detectedType === 'svg') {
      this.svgContent = this.processInlineSvg(this.name.trim());
    }

    // URL icons are rendered directly as <img> in render() to keep SSR and
    // client hydration deterministic for slotted icons.
  }

  /**
   * Parses the icon name to extract category and file name.
   * Supports multi-level directories using -- as separator.
   * For example: 'components--video--play' → /assets/icons/components/video/play.svg
   * If no separator, icon is loaded from /assets/icons/{name}.svg
   */
  private getIconPath(): string {
    const parts = this.name.split('--');
    if (parts.length > 1) {
      // All parts except the last are directories, the last is the file name
      const dir = parts.slice(0, -1).join('/');
      const file = parts[parts.length - 1];
      return getAssetPath(`../assets/icons/${dir}/${file}.svg`);
    }
    return getAssetPath(`../assets/icons/${this.name}.svg`);
  }

  /**
   * Processes inline SVG to remove fill attributes so color prop can work.
   * Preserves fill attributes with specific colors (hex codes, named colors) to maintain original styling.
   * Removes standard colors (black, white) to allow color inheritance.
   * Adds data attribute to indicate if SVG has specific fill colors that should prevent currentcolor inheritance.
   */
  private processInlineSvg(svgString: string): string {
    // Standard colors that should be removed to allow inheritance
    const standardColors = ['black', 'white', 'currentcolor', 'none'];

    // Find all fill attributes
    const fillColorPattern = /fill=["']([^"']+)["']/gi;
    const allMatches = [...svgString.matchAll(fillColorPattern)];

    if (allMatches.length > 0) {
      // Check if there are any non-standard colors (hex codes or named colors that aren't standard)
      const hasSpecificColors = allMatches.some(match => {
        const color = match[1]?.toLowerCase();
        if (!color) return false;
        // Standard colors should be removed
        if (standardColors.includes(color)) return false;
        // Hex codes or other named colors are specific
        return color.startsWith('#') || /^[a-zA-Z]{3,}$/.test(color);
      });

      // If there are specific colors (non-standard), preserve them and add data-no-inherit
      if (hasSpecificColors) {
        // Add data attribute to root SVG element to indicate it has specific colors
        return svgString.replace(/<svg([^>]*)>/i, (match, attrs) => {
          // Check if data-no-inherit already exists
          if (/data-no-inherit/.test(attrs)) {
            return match;
          }
          return `<svg${attrs} data-no-inherit="true">`;
        });
      }

      // Remove standard colors to allow inheritance.
      // Stroke-based SVGs: preserve fill="none" so transparent areas stay clear.
      // Fill-based SVGs: add explicit fill="currentColor" to root <svg> for text color inheritance.
      const usesStroke = /\bstroke\s*=/.test(svgString);
      let processed = svgString;
      standardColors.forEach(color => {
        if (color === 'none' && usesStroke) return;
        const pattern = new RegExp(`fill=["']${color}["']`, 'gi');
        processed = processed.replace(pattern, '');
      });

      // Fill-based icons need explicit currentColor so they inherit text color
      if (!usesStroke) {
        processed = processed.replace(/<svg([^>]*)>/i, '<svg$1 fill="currentColor">');
      }
      return processed;
    }

    // No fill attributes found, return as is
    return svgString;
  }

  /**
   * Lifecycle method that runs when the component is loaded.
   * Handles async operations like fetching icons from URLs or files.
   */
  componentDidLoad() {
    // Skip loading if already inlined during SSR
    if (this.host.hasAttribute('data-inlined')) {
      return;
    }

    // Inline SVG and URL icons are already processed in componentWillLoad
    if (this.iconType === 'svg' || this.iconType === 'url') {
      return;
    }

    // For icon names, use lazy loading or load immediately
    if (!this.isLazy) {
      this.loadIcon();
    } else {
      // setupIntersectionObserver already includes an immediate viewport check,
      // so no extra manual check or setTimeout is needed.
      this.setupIntersectionObserver();
    }
  }

  /**
   * Lifecycle method that runs after each render.
   * Ensures color is applied after SVG is in the DOM.
   * Note: Color updates are also handled by @Watch('color') decorator.
   */
  componentDidRender() {
    // Only update color if we have both color prop and SVG content
    // Watch handler will handle color prop changes, this handles initial render
    if (this.color && this.svgContent && this.iconContainer) {
      this.updateIconColor();
    }
  }

  /**
   * Lifecycle method that runs when the component is removed from the DOM.
   * Cleans up the intersection observer to prevent memory leaks.
   */
  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Watches for changes in the name prop to reload the icon
   */
  @Watch('name')
  watchNameHandler() {
    const newIconType = this.detectIconType();

    // Only update if type changed or if we need to reload
    if (newIconType !== this.iconType) {
      this.iconType = newIconType;
    }

    if (this.iconType === 'svg') {
      this.svgContent = this.processInlineSvg(this.name.trim());
    } else if (this.iconType === 'name') {
      this.loadIcon();
    }
    // 'url' icons are rendered directly as <img> in render() — nothing to fetch.
  }

  /**
   * Watches for changes in the color prop to update the icon color
   */
  @Watch('color')
  watchColorHandler() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (this.svgContent && this.iconContainer) {
        this.updateIconColor();
      }
    });
  }

  /**
   * Sets up intersection observer for lazy loading.
   * Loads the icon only when it enters the viewport.
   */
  private setupIntersectionObserver() {
    // Skip IntersectionObserver setup in SSR environment
    if (!Build.isBrowser || typeof IntersectionObserver === 'undefined') {
      return;
    }

    // Only set up observer for icon names, not for inline SVG or URLs
    if (this.iconType !== 'name' || !this.iconContainer) return;

    this.observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          this.loadIcon();
          this.observer?.disconnect();
        }
      },
      { threshold: 0.1 },
    );

    this.observer.observe(this.iconContainer);

    // Deferred viewport check — avoids forced synchronous reflow during componentDidLoad.
    // IntersectionObserver handles most cases; this covers the edge case where the
    // observer callback doesn't fire for already-visible elements on first render.
    requestAnimationFrame(() => {
      if (!this.iconContainer || !this.observer) return;
      const rect = this.iconContainer.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0) {
        this.loadIcon();
        this.observer?.disconnect();
      }
    });
  }

  /**
   * Loads the SVG icon from the appropriate path based on the name format.
   * Handles both categorized and non-categorized icons.
   */
  private async loadIcon() {
    if (!this.name) {
      console.error(`${COMPONENT_NAME}: name attribute is required`);
      return;
    }

    // Only named icons are fetched from /assets/icons. 'url' icons render as <img>
    // and 'svg' icons are inlined — never build an asset path for them (a URL name
    // would otherwise produce /assets/icons/https://….svg.svg → 404).
    if (this.iconType !== 'name') {
      this.iconLoading = false;
      return;
    }

    // Guard: prevent duplicate calls (e.g. IntersectionObserver + manual viewport check)
    if (this.iconLoading || this.svgContent) {
      return;
    }
    this.iconLoading = true;

    // CRITICAL: Never load icons during SSR - always skip and load on client
    // This prevents binary data from being included in SSR HTML
    if (!Build.isBrowser || typeof window === 'undefined' || typeof fetch === 'undefined') {
      this.iconLoading = false;
      return;
    }

    const iconPath = this.getIconPath();

    try {
      const response = await fetch(iconPath);
      if (!response.ok) throw new Error(`Failed to load icon: ${this.name} at path: ${iconPath}`);

      // Check content type to ensure it's SVG, not binary
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('svg') && !contentType.includes('xml') && !contentType.includes('text')) {
        console.warn(`${COMPONENT_NAME}: Expected SVG but got ${contentType} for icon: ${this.name}`);
        return;
      }

      let svgText = await response.text();

      // Validate that we got actual SVG text, not binary data
      if (!svgText.trim().startsWith('<svg') && !svgText.includes('<svg')) {
        console.warn(`${COMPONENT_NAME}: Invalid SVG content for icon: ${this.name}`);
        return;
      }

      // Process SVG similar to inline SVG - preserve fill with specific colors
      svgText = this.processInlineSvg(svgText);

      this.svgContent = svgText;
    } catch (error) {
      // Only log errors in browser environment
      if (Build.isBrowser && typeof window !== 'undefined') {
        console.error('Error loading icon:', error);
      }
    } finally {
      this.iconLoading = false;
    }
  }

  /**
   * Updates the SVG fill color if color prop is provided.
   * Applies the color to the root SVG element.
   * Only applies if SVG doesn't have data-no-inherit attribute.
   */
  private updateIconColor() {
    if (!this.color || !this.iconContainer) return;

    const svg = this.iconContainer.querySelector('svg');
    if (svg && !svg.hasAttribute('data-no-inherit')) {
      svg.style.fill = this.color;
    }

    // Note: img elements don't support fill, so we don't handle them here
  }

  private getNumericDimension(value?: string): number | undefined {
    if (!value) return undefined;

    const numericValue = parseFloat(value);
    return isNaN(numericValue) ? undefined : Math.round(numericValue);
  }

  /**
   * Resolves the alt text for URL-based icons. Uses the explicit `alt` prop when
   * provided, otherwise derives a human-readable label from the icon file name
   * so the rendered <img> never ships with an empty alt attribute.
   */
  private getResolvedAlt(): string {
    if (this.alt) return this.alt;

    const fileName = this.name.trim().split('/').pop() || '';
    const baseName = fileName.replace(/\.svg$/i, '');
    const humanized = baseName
      .split(/[-_]/)
      .filter(Boolean)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    return humanized ? `${humanized} icon` : 'Icon';
  }

  render() {
    // Use innerHTML directly - Stencil handles hydration correctly when innerHTML is set in render
    // The key is to ensure svgContent is set synchronously in componentWillLoad for inline SVG
    // For async-loaded icons (name type), svgContent starts empty and is set later, avoiding conflicts

    // Apply width and height as inline styles if provided
    const inlineStyles: { [key: string]: string } = {};
    const normalizedWidth = this.normalizeSize(this.width);
    const normalizedHeight = this.normalizeSize(this.height);

    if (normalizedWidth) {
      inlineStyles.width = normalizedWidth;
    }
    if (normalizedHeight) {
      inlineStyles.height = normalizedHeight;
    }

    const hasInlineStyles = Object.keys(inlineStyles).length > 0;

    // If icon was inlined during SSR, use slot to preserve server content
    if (this.host.hasAttribute('data-inlined')) {
      return <slot />;
    }

    if (this.iconType === 'url') {
      return (
        <div
          ref={el => (this.iconContainer = el as HTMLDivElement)}
          class={`${COMPONENT_NAME} ${this.class || ''}`.trim()}
          style={hasInlineStyles ? inlineStyles : undefined}
        >
          <img
            src={this.name.trim()}
            loading={this.isLazy ? 'lazy' : undefined}
            width={this.getNumericDimension(this.width)}
            height={this.getNumericDimension(this.height)}
            alt={this.getResolvedAlt()}
          />
        </div>
      );
    }

    return (
      <div
        ref={el => (this.iconContainer = el as HTMLDivElement)}
        class={`${COMPONENT_NAME} ${this.class || ''}`.trim()}
        style={hasInlineStyles ? inlineStyles : undefined}
        innerHTML={this.svgContent}
      />
    );
  }
}
